import React from "react";
import { Badge, Table } from "reactstrap";

export function MethodBadge({ method }) {
  const cls =
    method === "GET"
      ? "get"
      : method === "POST"
        ? "post"
        : method === "PUT"
          ? "put"
          : "delete";
  return <span className={"qt-api-method qt-api-method--" + cls}>{method}</span>;
}

export function CodeBlock({ label, children }) {
  return (
    <div>
      {label && <div className="qt-api-code__label">{label}</div>}
      <pre className="qt-api-code">{children}</pre>
    </div>
  );
}

export function Endpoint({ ep }) {
  return (
    <div className="qt-api-endpoint" id={ep.path.replace(/[^a-z0-9]/gi, "-")}>
      <div className="qt-api-endpoint__head">
        <MethodBadge method={ep.method} />
        <code className="qt-api-endpoint__path">{ep.path}</code>
        <span className="qt-api-endpoint__auth">
          {ep.auth === false ? "Public" : ep.auth === true ? "Session required" : ep.auth}
        </span>
      </div>
      <div className="qt-api-endpoint__body">
        <p className="text-muted mb-2" style={{ fontSize: 14 }}>
          {ep.summary}
        </p>
        {ep.note && (
          <p className="small text-muted mb-2">
            <em>{ep.note}</em>
          </p>
        )}
        {ep.body && <CodeBlock label="Request body">{ep.body}</CodeBlock>}
        {ep.response && <CodeBlock label="Example response">{ep.response}</CodeBlock>}
      </div>
    </div>
  );
}

export function partnerCurlExample(liveBase, method, path, body) {
  const url = liveBase.replace(/\/$/, "") + path;
  const lines = [`curl -X ${method} ${url} \\`];
  lines.push('  -H "Content-Type: application/json" \\');
  lines.push('  -H "Authorization: Bearer qt_live_YOUR_API_KEY" \\');
  if (body) {
    lines.push("  -d '" + body.replace(/\n/g, "").replace(/\s+/g, " ") + "'");
  } else {
    lines[lines.length - 1] = lines[lines.length - 1].replace(/ \\$/, "");
  }
  return lines.join("\n");
}

export function AudienceCallout({ children }) {
  return (
    <div className="qt-api-docs__audience">
      <div className="qt-api-docs__audience-label">In plain terms</div>
      <div className="qt-api-docs__audience-body">{children}</div>
    </div>
  );
}

export function CollapsibleTechnical({ title = "Technical details (for developers)", children }) {
  return (
    <details className="qt-api-docs__technical">
      <summary>{title}</summary>
      <div className="qt-api-docs__technical-body">{children}</div>
    </details>
  );
}

export function PlainFlow({ steps }) {
  return (
    <div className="qt-api-plain-flow">
      {steps.map((step, i) => (
        <div key={step.title} className="qt-api-plain-flow__item">
          <div className="qt-api-plain-flow__num">{i + 1}</div>
          <div>
            <div className="qt-api-plain-flow__title">{step.title}</div>
            <div className="qt-api-plain-flow__detail">{step.detail}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function StatusCards({ statuses }) {
  return (
    <div className="qt-api-status-grid">
      {statuses.map((s) => (
        <div key={s.code} className="qt-api-status-card">
          <div className="qt-api-status-card__plain">{s.plain}</div>
          <p className="qt-api-status-card__detail">{s.detail}</p>
          <code className="qt-api-status-card__code">{s.code}</code>
        </div>
      ))}
    </div>
  );
}

export function CompareTable() {
  return (
    <div className="qt-api-docs__section">
      <h2>Website embed vs Partner API</h2>
      <AudienceCallout>
        <strong>Website embed</strong> is the fastest drop-in: a quote button on product pages with hosted checkout.
        <strong> Partner API</strong> is for developers who want full control - custom website shops, mobile apps,
        marketplaces, ERP, TMS, and other backends.
      </AudienceCallout>
      <p className="text-muted">
        Not sure which to pick? Use the embed when you want minimal dev work. Use the Partner API when your team is
        building a bespoke shopper or operations experience.
      </p>
      <table className="table table-bordered qt-api-table bg-white">
        <thead className="table-light">
          <tr>
            <th></th>
            <th>Website embed</th>
            <th>Partner API</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Who</strong></td>
            <td>Online stores (Shopify, WooCommerce, custom sites)</td>
            <td>Custom storefronts, ERP, TMS, marketplaces, mobile apps</td>
          </tr>
          <tr>
            <td><strong>Best for</strong></td>
            <td>Shop owners and marketing teams</td>
            <td>Operations, IT, and integration partners</td>
          </tr>
          <tr>
            <td><strong>Goal</strong></td>
            <td>Delivery quote on product pages + hosted checkout</td>
            <td>Full programmatic quote → book → track</td>
          </tr>
          <tr>
            <td><strong>Integration</strong></td>
            <td>CDN script tag + optional checkout URL params</td>
            <td>REST <code>/v1/partner/*</code> with API keys</td>
          </tr>
          <tr>
            <td><strong>Setup</strong></td>
            <td><a href="/integrations">Integrations</a> → Website Widget</td>
            <td>Request access → provisioned keys</td>
          </tr>
          <tr>
            <td><strong>Docs</strong></td>
            <td><a href="/api-docs?product=embed">Website embed docs</a></td>
            <td><a href="/api-docs?product=partner">Partner API docs</a></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function SidebarActions({ primaryHref, primaryLabel, secondaryHref, secondaryLabel }) {
  return (
    <div className="qt-api-docs__sidebar-actions">
      <a href={secondaryHref} className="qt-api-docs__sidebar-btn qt-api-docs__sidebar-btn--secondary">
        {secondaryLabel}
      </a>
      <a href={primaryHref} className="qt-api-docs__sidebar-btn qt-api-docs__sidebar-btn--primary">
        {primaryLabel}
      </a>
    </div>
  );
}

export function ProductSwitcher({ product, onChange }) {
  return (
    <div className="qt-api-product-tabs">
      <button
        type="button"
        className={"qt-api-product-tab" + (product === "embed" ? " active" : "")}
        onClick={() => onChange("embed")}
      >
        Website embed
      </button>
      <button
        type="button"
        className={"qt-api-product-tab" + (product === "partner" ? " active" : "")}
        onClick={() => onChange("partner")}
      >
        Partner API
      </button>
    </div>
  );
}

/** Partner API field rows: { field, type, required, description, example? } */
export function FieldReferenceTable({ fields, lastColumnLabel = "Example / constraints" }) {
  const requiredLabel = (r) => {
    if (r.required === true || r.required === "yes") return "Yes";
    if (r.required === "recommended") return "Recommended";
    if (r.required === "conditional") return "Conditional";
    return "No";
  };

  return (
    <Table bordered responsive size="sm" className="qt-api-table bg-white">
      <thead className="table-light">
        <tr>
          <th>Field</th>
          <th>Type</th>
          <th>Required</th>
          <th>Description</th>
          <th>{lastColumnLabel}</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((f) => (
          <tr key={f.field}>
            <td>
              <code className={f.nested ? "qt-api-field-nested" : undefined}>{f.field}</code>
            </td>
            <td><code>{f.type}</code></td>
            <td>{requiredLabel(f)}</td>
            <td>{f.description}</td>
            <td className="small text-muted">{f.example || "-"}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function VehicleSlugTable({ slugs }) {
  return (
    <Table bordered responsive size="sm" className="qt-api-table bg-white">
      <thead className="table-light">
        <tr>
          <th>trucktype value</th>
          <th>Label</th>
          <th>Capacity</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        {slugs.map((v) => (
          <tr key={v.slug}>
            <td><code>{v.slug}</code></td>
            <td>{v.label}</td>
            <td>{v.tonnes} t</td>
            <td className="small text-muted">Use in POST /quotes when <code>sell: true</code> in catalog</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function CategorySlugTable({ categories }) {
  return (
    <Table bordered responsive size="sm" className="qt-api-table bg-white">
      <thead className="table-light">
        <tr>
          <th>category value</th>
          <th>Meaning</th>
        </tr>
      </thead>
      <tbody>
        {categories.map((c) => (
          <tr key={c.slug}>
            <td><code>{c.slug}</code></td>
            <td>{c.description}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function ErrorTable({ codes }) {
  return (
    <>
      <p className="text-muted">Standard HTTP status codes with JSON body <code>{`{ "message": "..." }`}</code>.</p>
      <table className="table table-bordered qt-api-table bg-white">
        <thead className="table-light">
          <tr><th>Status</th><th>Meaning</th></tr>
        </thead>
        <tbody>
          {codes.map(([code, desc]) => (
            <tr key={code}>
              <td>
                <Badge color={code === "429" ? "warning" : code.startsWith("4") ? "danger" : "secondary"}>
                  {code}
                </Badge>
              </td>
              <td>{desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
